import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ChevronDown, ChevronUp, ExternalLink, Radio,
  Wrench, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { cn } from "../lib/utils";

// Format seconds → "3m", "1h 22m"
function fmtDuration(sec) {
  if (!sec || sec <= 0) return "0s";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${s}s`;
}

const TONE = {
  operational: {
    strip: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200/60 dark:border-emerald-900/50",
    stripText: "text-emerald-900 dark:text-emerald-200",
    dot: "bg-emerald-500",
    livePill: "bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-200",
    ring: "border-sky-200/70 dark:border-sky-900/60",
    body: "bg-sky-50/70 dark:bg-sky-950/25",
  },
  maintenance: {
    strip: "bg-sky-50 dark:bg-sky-950/40 border-sky-200/60 dark:border-sky-900/50",
    stripText: "text-sky-900 dark:text-sky-200",
    dot: "bg-sky-500",
    livePill: "bg-sky-100 dark:bg-sky-900/60 text-sky-700 dark:text-sky-200",
    ring: "border-sky-200/70 dark:border-sky-900/60",
    body: "bg-sky-50/70 dark:bg-sky-950/25",
  },
  degraded: {
    strip: "bg-amber-50 dark:bg-amber-950/40 border-amber-200/60 dark:border-amber-900/50",
    stripText: "text-amber-900 dark:text-amber-200",
    dot: "bg-amber-500",
    livePill: "bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-200",
    ring: "border-sky-200/70 dark:border-sky-900/60",
    body: "bg-sky-50/70 dark:bg-sky-950/25",
  },
  outage: {
    strip: "bg-rose-50 dark:bg-rose-950/40 border-rose-200/60 dark:border-rose-900/50",
    stripText: "text-rose-900 dark:text-rose-200",
    dot: "bg-rose-500",
    livePill: "bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-200",
    ring: "border-sky-200/70 dark:border-sky-900/60",
    body: "bg-sky-50/70 dark:bg-sky-950/25",
  },
};

function ProbeCard({ check, windowLabel }) {
  const isPlaceholder = !!check.is_placeholder;
  const state = check.state || (isPlaceholder ? "pending" : "operational");
  const loginUrl = check.login_url;
  const uptimeValue = check.uptime_pct ?? check.uptime_30d_pct;
  const outages = check.outages ?? check.outages_30d;
  const downtime = check.downtime_seconds ?? check.downtime_seconds_30d;
  const uptime = typeof uptimeValue === "number" ? `${uptimeValue.toFixed(3)}%` : "—";

  // "Not activated yet" — same body as pending; green header when a login URL is set,
  // red when the link isn't set up yet
  if (state === "not_activated") {
    const hasLogin = !!loginUrl;
    const Wrapper = hasLogin ? "a" : "div";
    const linkProps = hasLogin ? { href: loginUrl, target: "_blank", rel: "noreferrer noopener" } : {};
    return (
      <Wrapper
        {...linkProps}
        className={cn(
          "rounded-md overflow-hidden bg-muted/40 border block",
          hasLogin
            ? "border-emerald-500/40 hover:border-emerald-500/70 hover:bg-emerald-500/5 transition-colors cursor-pointer"
            : "border-dashed border-rose-400/60"
        )}
        data-testid={`status-probe-${check.id}`}
      >
        <div
          className={cn(
            "flex items-center justify-between px-3 py-1.5 text-white",
            hasLogin ? "bg-emerald-500" : "bg-rose-500"
          )}
        >
          <div className="text-[12.5px] font-bold uppercase tracking-wide truncate">{check.name}</div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wider">
            {hasLogin ? "Login" : "Not Activated"}
          </div>
        </div>
        <div className="min-h-[74px] flex items-center justify-center text-center px-3">
          <div>
            <div className="text-[11.5px] font-medium text-foreground/80">Awaiting Pingdom probe</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5">
              {hasLogin ? "Click to open login" : "Will populate once check is configured"}
            </div>
          </div>
        </div>
        <div
          className={cn(
            "px-3 py-1.5 text-[10.5px] text-muted-foreground border-t flex items-center justify-between",
            hasLogin ? "border-emerald-500/30" : "border-rose-400/40"
          )}
        >
          <span>{hasLogin ? "External login ready" : "Link not set up yet"}</span>
          {hasLogin && <ExternalLink className="w-3 h-3" />}
        </div>
      </Wrapper>
    );
  }

  // Operational external tile (no Pingdom stats but marked live — e.g. Cloudflare dashboard)
  if (isPlaceholder && state === "operational") {
    const Wrapper = loginUrl ? "a" : "div";
    const linkProps = loginUrl ? { href: loginUrl, target: "_blank", rel: "noreferrer noopener" } : {};
    return (
      <Wrapper
        {...linkProps}
        className={cn(
          "rounded-md overflow-hidden border border-emerald-500/40 bg-emerald-500/5 block",
          loginUrl && "hover:border-emerald-500/70 hover:bg-emerald-500/10 transition-colors cursor-pointer"
        )}
        data-testid={`status-probe-${check.id}`}
      >
        <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-500 text-white">
          <div className="text-[12.5px] font-bold uppercase tracking-wide truncate">{check.name}</div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wider">Live</div>
        </div>
        <div className="min-h-[74px] flex items-center justify-center text-center px-3">
          <div>
            <div className="text-[13px] font-semibold text-emerald-700 dark:text-emerald-300">Operational</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5">{loginUrl ? "Click to open dashboard" : ""}</div>
          </div>
        </div>
        <div className="px-3 py-1.5 text-[10.5px] text-muted-foreground border-t border-emerald-500/30 flex items-center justify-between">
          <span>External monitor</span>
          {loginUrl && <ExternalLink className="w-3 h-3" />}
        </div>
      </Wrapper>
    );
  }

  // Default "pending" placeholder
  if (isPlaceholder) {
    return (
      <div className="rounded-md overflow-hidden bg-muted/40 border border-dashed border-border" data-testid={`status-probe-${check.id}`}>
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted text-muted-foreground">
          <div className="text-[12.5px] font-bold uppercase tracking-wide truncate">{check.name}</div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wider">Pending</div>
        </div>
        <div className="min-h-[74px] flex items-center justify-center text-center px-3">
          <div>
            <div className="text-[11.5px] font-medium text-foreground/80">Awaiting Pingdom probe</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5">Will populate once check is configured</div>
          </div>
        </div>
        <div className="px-3 py-1.5 text-[10.5px] text-muted-foreground border-t border-border">
          Placeholder · configure in Pingdom
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md overflow-hidden bg-neutral-900 border border-neutral-800 shadow-sm" data-testid={`status-probe-${check.id}`}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-amber-500 text-white">
        <div className="text-[12.5px] font-bold uppercase tracking-wide truncate">{check.name}</div>
        <div className="text-[10.5px] font-semibold uppercase tracking-wider">Ping</div>
      </div>
      <div className="grid grid-cols-5">
        {/* Left: mini uptime gradient bar */}
        <div className="col-span-3 relative overflow-hidden bg-emerald-800">
          <div className="absolute top-1.5 left-2 text-[9.5px] font-semibold text-emerald-100/90">100%</div>
          <div className="h-full min-h-[80px] w-full bg-gradient-to-t from-emerald-500/60 to-emerald-700/20" />
        </div>
        <div className="col-span-2 bg-neutral-900 text-white divide-y divide-neutral-800">
          <div className="px-2.5 py-1">
            <div className="text-[9px] uppercase tracking-wide text-neutral-400">Uptime</div>
            <div className="text-[13.5px] font-extrabold text-amber-400 leading-tight">{uptime}</div>
          </div>
          <div className="px-2.5 py-1">
            <div className="text-[9px] uppercase tracking-wide text-neutral-400">Outages</div>
            <div className="text-[13.5px] font-extrabold text-amber-400 leading-tight">{outages ?? 0}</div>
          </div>
          <div className="px-2.5 py-1">
            <div className="text-[9px] uppercase tracking-wide text-neutral-400">Downtime</div>
            <div className="text-[13.5px] font-extrabold text-amber-400 leading-tight">{fmtDuration(downtime)}</div>
          </div>
        </div>
      </div>
      <div className="px-3 py-1 text-[10px] text-neutral-400 bg-neutral-900 border-t border-neutral-800 flex items-center justify-between">
        <span>{windowLabel || "Last 30 days"} · Pingdom</span>
        <span>Every 1 min</span>
      </div>
    </div>
  );
}

export default function GlobalStatusBar() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(() => localStorage.getItem("ucv_status_window") || "30d");
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/status/summary?window=${encodeURIComponent(window)}`);
      setData(data);
    } catch { /* ignore — status is best-effort */ }
    finally { setLoading(false); }
  }, [window]);

  useEffect(() => {
    load();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(load, 60000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  const onWindowChange = (w) => {
    setWindow(w);
    try { localStorage.setItem("ucv_status_window", w); } catch { /* ignore */ }
  };

  const status = data?.status || "operational";
  const label = data?.label || "All Systems Operational";
  const tone = TONE[status] || TONE.operational;

  return (
    <div className={cn("w-full border-b", tone.strip)} data-testid="global-status-bar">
      {/* Slim always-visible strip — click to expand */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left"
        data-testid="global-status-toggle"
        aria-expanded={expanded}
      >
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-[13px]">
          <div className="flex items-center gap-3 min-w-0">
            <span className="relative inline-flex shrink-0">
              <span className={cn("inline-block w-2.5 h-2.5 rounded-full pulse-dot", tone.dot)} />
            </span>
            <span className={cn("font-medium truncate", tone.stripText)}>
              {loading && !data ? "Loading status…" : label}
            </span>
            {data?.live && (
              <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", tone.livePill)}>
                <Radio className="w-3 h-3" /> LIVE
              </span>
            )}
          </div>
          <div className={cn("inline-flex items-center gap-1 text-[12px]", tone.stripText, "opacity-80")}>
            {expanded ? "Hide" : "Details"}
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </div>
        </div>
      </button>

      {/* Expanded content — inline below the strip */}
      {expanded && (
        <div className={cn("border-t", tone.ring, tone.body)}>
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-4 space-y-4">
            {/* Current status + Open status page */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Current Status</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className={cn("inline-block w-2.5 h-2.5 rounded-full", tone.dot)} />
                  <span className={cn("text-[14px] font-semibold", tone.stripText)}>{label}</span>
                </div>
                <div className="text-[11.5px] text-muted-foreground mt-0.5">
                  Level: <span className="font-medium">{status}</span> · Source: <span className="font-medium">{data?.source || "—"}</span>
                  {data?.live && <> (live, auto-refresh {data.auto_refresh_seconds || 60}s)</>}
                </div>
              </div>
              {data?.page_url && (
                <a
                  href={data.page_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-[12px] text-primary hover:underline inline-flex items-center gap-1"
                  data-testid="status-open-page-link"
                >
                  Open status page <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {/* Active incidents / maintenances */}
            {(data?.active_incidents?.length > 0 || data?.active_maintenances?.length > 0) && (
              <div className="rounded-lg border border-amber-300/60 dark:border-amber-500/30 bg-amber-50/80 dark:bg-amber-500/10 px-3.5 py-2.5 space-y-1.5">
                {(data.active_incidents || []).map((i) => (
                  <div key={i.id} className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-amber-900 dark:text-amber-100">{i.title}</div>
                      <a href={i.link} target="_blank" rel="noreferrer noopener" className="text-[11.5px] text-amber-700 dark:text-amber-300 hover:underline">View update</a>
                    </div>
                  </div>
                ))}
                {(data.active_maintenances || []).map((m) => (
                  <div key={m.id} className="flex items-start gap-2">
                    <Wrench className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold">{m.title}</div>
                      <a href={m.link} target="_blank" rel="noreferrer noopener" className="text-[11.5px] hover:underline">View update</a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Uptime stats — grouped rows (Ottawa / Calgary / Montreal) */}
            {data?.groups?.length > 0 && (
              <div>
                <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
                  <div className="text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground inline-flex items-center gap-1.5">
                    <Radio className="w-3 h-3" /> Uptime stats
                    <span className="text-muted-foreground/70 normal-case tracking-normal font-normal">— including regularly scheduled maintenance, with failover</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10.5px] text-muted-foreground">{data.window_label || "Last 30 days"} · Pingdom</span>
                    <select
                      value={window}
                      onChange={(e) => onWindowChange(e.target.value)}
                      className="h-7 text-[11.5px] rounded-md border border-border bg-background/70 px-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="status-window-select"
                    >
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                      <option value="90d">Last 90 days</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  {(() => {
                    const maxCols = Math.max(1, ...data.groups.map((gg) => gg.members.length));
                    return data.groups.map((g) => (
                      <div key={g.key} data-testid={`status-group-${g.key}`}>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                          {g.label}
                        </div>
                        <div
                          className="grid gap-3"
                          style={{ gridTemplateColumns: `repeat(${maxCols}, minmax(0, 1fr))` }}
                        >
                          {g.members.map((c) => <ProbeCard key={c.id} check={c} windowLabel={data.window_label} />)}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
